/**
 * Session Management for GHL SSO Authentication
 * 
 * Handles GHL postMessage for SSO token and stores in session.
 * Requirements: 8.2
 */

const TOKEN_KEY = 'aha_sso_token';
const LOCATION_KEY = 'aha_location_id';
const USER_DATA_KEY = 'aha_user_data';

// API base URL for backend
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://actually-helpful-assistant.rawsrv.workers.dev';

interface SSOData {
  token: string;
  locationId: string;
}

interface GHLUserData {
  userId: string;
  companyId: string;
  role: string;
  type: string;
  activeLocation?: string;
  userName: string;
  email: string;
  isAgencyOwner: boolean;
}

let ssoData: SSOData | null = null;

/**
 * Initialize SSO listener for GHL iframe postMessage
 */
export function initSSOListener(): void {
  window.addEventListener('message', handleSSOMessage);
  
  // Try to restore from session storage
  const storedToken = sessionStorage.getItem(TOKEN_KEY);
  const storedLocation = sessionStorage.getItem(LOCATION_KEY);
  
  if (storedToken && storedLocation) {
    ssoData = {
      token: storedToken,
      locationId: storedLocation,
    };
  } else {
    // Request user data from GHL parent window
    requestUserDataFromGHL();
  }
}

/**
 * Request user data from GHL parent window via postMessage
 */
function requestUserDataFromGHL(): void {
  // Check if we're in an iframe
  if (window.parent !== window) {
    console.log('[SSO] Requesting user data from GHL parent window');
    window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');
  }
}

/**
 * Handle SSO message from GHL parent window
 */
function handleSSOMessage(event: MessageEvent): void {
  const data = event.data;
  
  // Handle GHL's REQUEST_USER_DATA_RESPONSE
  if (data?.message === 'REQUEST_USER_DATA_RESPONSE' && data.payload) {
    console.log('[SSO] Received encrypted user data from GHL');
    decryptAndStoreUserData(data.payload);
    return;
  }
  
  // Also handle legacy/custom SSO token format
  if (data?.type === 'GHL_SSO_TOKEN' && data.token && data.locationId) {
    ssoData = {
      token: data.token,
      locationId: data.locationId,
    };
    
    // Store in session
    sessionStorage.setItem(TOKEN_KEY, data.token);
    sessionStorage.setItem(LOCATION_KEY, data.locationId);
    
    // Dispatch custom event for components to react
    window.dispatchEvent(new CustomEvent('sso-authenticated', { detail: ssoData }));
  }
}

/**
 * Decrypt user data on the backend and store the result
 */
async function decryptAndStoreUserData(encryptedData: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/sso/decrypt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ encryptedData }),
    });
    
    if (!response.ok) {
      console.error('[SSO] Failed to decrypt user data:', response.status);
      return;
    }
    
    const userData: GHLUserData = await response.json();
    console.log('[SSO] User data decrypted successfully');
    
    // Generate a session token from the user data
    const locationId = userData.activeLocation || userData.companyId;
    const token = btoa(JSON.stringify({
      userId: userData.userId,
      locationId,
      email: userData.email,
      timestamp: Date.now(),
    }));
    
    ssoData = {
      token,
      locationId,
    };
    
    // Store in session
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(LOCATION_KEY, locationId);
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    
    // Dispatch custom event for components to react
    window.dispatchEvent(new CustomEvent('sso-authenticated', { detail: ssoData }));
  } catch (error) {
    console.error('[SSO] Error decrypting user data:', error);
  }
}

/**
 * Get the current auth token
 */
export function getAuthToken(): string | null {
  return ssoData?.token || null;
}

/**
 * Get the current location ID
 */
export function getLocationId(): string | null {
  return ssoData?.locationId || null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return ssoData !== null;
}

/**
 * Clear session data
 */
export function clearSession(): void {
  ssoData = null;
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(LOCATION_KEY);
}

/**
 * Set auth data manually (for development/testing)
 */
export function setAuthData(token: string, locationId: string): void {
  ssoData = { token, locationId };
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(LOCATION_KEY, locationId);
}
